import React, { useState, useEffect, useCallback } from "react";
import { Button, Space, Spin, Table, Input, Tooltip } from "antd";
import userServiceApi from "../../../../api/userServiceApi";
import { getRequest, deleteRequest } from "../../../../api/http";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "../index.scss";
import deleteIcon from "../../../../assets/images/delete-icon.svg";
import editIcon from "../../../../assets/images/edit-icon.svg";
import ModalMediaPlayer from "../../../videoPlayer/modalPlayer";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";
import { inputFieldCharLimit } from "../../../../config/index";
import { debounce } from 'lodash';
import SynthesiaImage from '../../../../assets/images/synthesia.jpg';

export default function HelpSectionComponent({ userRole, title }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [tabData, setTabData] = useState([]); // Initialize as empty array
  const [mediatype, setmediatype] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [helpDataResponse, setHelpDataResponse] = useState(
    sessionStorage.getItem("helpDataResponse") === "success" ? true : false
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [reload, setReload] = useState(false);

  useEffect(() => {
    getHelpInfo();
  }, []);

  // Ultra-strict sanitization
  const sanitizeSearchInput = (input) => {
    if (input === undefined || input === null) return '';
    if (typeof input !== 'string') return '';

    // Only allow: letters, numbers, spaces, hyphens, underscores
    const sanitized = input.replace(/[^a-zA-Z0-9\s\-_]/g, '');

    console.log('🔍 Search Sanitization - Input:', `"${input}"`, 'Output:', `"${sanitized}"`);
    return sanitized;
  };

  // Safe URL parameter encoding
  const getSafeSearchParam = (input) => {
    if (!input || input.trim() === '') return '';

    const sanitized = sanitizeSearchInput(input.trim());
    if (!sanitized) return '';

    return encodeURIComponent(sanitized);
  };

  useEffect(() => {
    if (sessionStorage.getItem("helpDataResponse") === "success") {
      setHelpDataResponse(true);
    } else {
      setHelpDataResponse(false);
    }
  }, [sessionStorage.getItem("helpDataResponse")]);

  const convertToStandardURL = (shortUrl) => {
    if (shortUrl?.indexOf("youtu.be") !== -1) {
      const video_id = shortUrl?.split("youtu.be/")[1].split("?")[0];
      return `https://www.youtube.com/watch?v=${video_id}`;
    }
    return shortUrl;
  };

  const PlayVideoHandler = (url, mediaType) => {
    setmediatype(mediaType);
    setVideoUrl(url);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setVideoUrl("");
  };

  useEffect(() => {
    sessionStorage.setItem("userRole", userRole);
  }, [userRole]);

  const getHelpInfo = async (searchValue = null) => {
    try {
      setLoading(true);

      let url;
      const currentSearchTerm = searchValue ?? searchTerm;

      if (currentSearchTerm && currentSearchTerm.trim() !== '') {
        const safeSearchParam = getSafeSearchParam(currentSearchTerm);
        if (!safeSearchParam) {
          // If search term becomes empty after sanitization, get all data
          url = userServiceApi.getHelpInfo(userRole);
        } else {
          url = userServiceApi.getFilteredHelpInfo(userRole, safeSearchParam);
        }
      } else {
        url = userServiceApi.getHelpInfo(userRole);
      }

      console.log('🌐 API URL:', url);

      const response = await getRequest(url);
      if (response) {
        setTabData(response.data || []); // Ensure it's always an array
      }
    } catch (error) {
      console.error("Error fetching help info:", error);
      toast.error("Failed to load help data");
      setTabData([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value) => {
      getHelpInfo(value);
    }, 600),
    [userRole]
  );

  // Search change handler
  const handleSearchChange = (e) => {
    const rawValue = e.target.value || '';
    const sanitizedValue = sanitizeSearchInput(rawValue);

    setSearchTerm(sanitizedValue);

    if (sanitizedValue.trim().length > 0) {
      debouncedSearch(sanitizedValue);
    } else {
      // If search is cleared, get all data
      getHelpInfo('');
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    getHelpInfo('');
  };

  useEffect(() => {
    getHelpInfo();
  }, [state?.helpdata, helpDataResponse, reload, userRole]);



  const deleteSingleItem = async (itemId) => {
    const headers = {
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    };
    try {
      const response = await deleteRequest(
        userServiceApi.deleteHelp(itemId),
        headers
      );

      if (response) {
        getHelpInfo();
        toast.success("Item deleted successfully");
      } else {
        toast.error("Try again something went wrong while deleting");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddNewHelp = () => {
    sessionStorage.removeItem("helpDataResponse");
    navigate(`/add-help`, {
      state: { userRole: userRole },
    });
  };

  const handleEditClick = (help) => {
    if (help) {
      sessionStorage.removeItem("helpDataResponse");
      navigate(`/edit-help/${help.id}`, {
        state: { helpData: help, userRole: userRole },
      });
    }
  };

  const typeChange = (type) => {
    const typeMap = {
      "SOCIAL_MEDIA": "Youtube Video",
      "VIMEO_MEDIA": "Vimeo Video",
      "IMAGE": "Image"
    };
    return typeMap[type] || type;
  };

  const assetTypeChange = (type) => {
    const assetMap = {
      "BUSINESS_CARD": "Business card",
      "CAROUSEL": "Carousel",
      "ABOUT_US": "About us",
      "PRODUCT": "Product",
      "SERVICE": "Service",
      "APPRENTICESHIP": "Apprenticeship",
      "VACANCY": "Vacancy",
      "COURSES": "Courses",
      "CONTRACTS": "Contract",
      "WHY_WORK": "Why work for us",
      "TEAMS_AND_ROLES": "Teams and roles",
      "WORK_EXPERIENCE": "Experience of work"
    };
    return assetMap[type] || type;
  };

  const extractSynthesiaId = (url) => {
    const match = url.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
    return match ? match[0] : null;
  };
  console.log('i am here')
  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text, record) => <div style={{ fontSize: "13px" }}>{text}</div>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => (
        <div
          style={{
            fontSize: "13px",
            width: "300px",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: ["mediaLink", "mediaType"],
      key: "mediaType",
      render: (text) => (
        <div style={{ fontSize: "13px" }}>{typeChange(text)}</div>
      ),
    },
    {
      title: "Feature",
      dataIndex: "assetTypeEnum",
      key: "assetTypeEnum",
      render: (text, record) => {
        const formatted = record.assetTypeEnum
          ?.toLowerCase()
          .replace(/_/g, " ")
          .replace(/\b\w/g, c => c.toUpperCase());

        return <div style={{ fontSize: "13px" }}>{formatted}</div>;
      },
    },
    {
      title: "Media",
      key: "action",
      render: (text, record) => (
        <Space
          size="middle"
          style={{ height: 60, display: "flex", alignItems: "center" }}
        >
          {console.log('record media link', record)}
          {record?.mediaLink?.mediaType === "SOCIAL_MEDIA" &&
            record?.mediaLink?.url ? (
            <div>
              {(() => {
                const standardURL = convertToStandardURL(record.mediaLink?.url);
                let videoID = new URLSearchParams(
                  new URL(standardURL).search
                ).get("v");
                if(!videoID){
                  const checkIfShorts = standardURL?.split('/shorts/')
                  videoID = checkIfShorts?.[1]?.split("?")[0] || null
                }
                return (
                  <div
                    className="media"
                    style={{ objectFit: "contain", cursor: "pointer" }}
                    onClick={() =>
                      PlayVideoHandler(
                        standardURL,
                        record?.mediaLink?.mediaType
                      )
                    }
                  >
                    <img
                      src={`https://img.youtube.com/vi/${videoID}/hqdefault.jpg`}
                      alt=""
                      className="media-image"
                      style={{ height: "100%", borderRadius: "8px" }}
                    />
                  </div>
                );
              })()}
            </div>
          ) : record?.mediaLink?.mediaType === "VIMEO_MEDIA" &&
            record?.mediaLink?.url ? (
            <div
              className="media"
              style={{ objectFit: "contain", cursor: "pointer" }}
              onClick={() => {
                PlayVideoHandler(
                  record?.mediaLink?.url,
                  record?.mediaLink?.mediaType
                );
              }}
            >
              <img
                src={`https://vumbnail.com/${record?.mediaLink?.url?.split("/")[4]}.jpg`}
                className="media-image"
                alt="Vimeo thumbnail"
              />
            </div>
          ) : record?.mediaLink?.mediaType === "IMAGE" &&
            record?.mediaLink?.url ? (
            <div className="media" style={{ objectFit: "contain" }}>
              <img
                src={record?.mediaLink?.url}
                alt=""
                className="media-image"
              />
            </div>
          ) : 
         
          
            record?.mediaLink?.mediaType === "SYNTHESIA_MEDIA" &&
record?.mediaLink?.url ? (
  (() => {
    const videoId = extractSynthesiaId(record.mediaLink.url);

    if (!videoId) return null;

    return (
      <div
        className="media"
        style={{ objectFit: "contain", cursor: "pointer" }}
        onClick={() =>
          PlayVideoHandler(
            `https://share.synthesia.io/embeds/videos/${videoId}?autoplay=1`,
            record.mediaLink.mediaType
          )
        }
      >
        <img
          className="media-image"
          style={{ height: "100%", borderRadius: "8px" }}
          src={ record.mediaLink?.thumbnailUrl ||SynthesiaImage}
          alt="Synthesia Video"
        />
      </div>
    );
  })()
) 
          
          
          : (
            <div style={{ height: "60px" }}></div>
          )}
        </Space>
      ),
    },
    {
      title: " ",
      key: "action",
      render: (text, record) => (
        <Space size="middle" style={{ height: 70 }}>
          <img
            src={editIcon}
            alt="editIcon"
            onClick={() => handleEditClick(record)}
            style={{ cursor: "pointer" }}
          />
          <div className="line" />
          <img
            src={deleteIcon}
            alt="deleteIcon"
            onClick={() => deleteSingleItem(record.id)}
            style={{ cursor: "pointer" }}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    setReload(state?.reload ? state?.reload : false);
  }, [state?.reload]);

  return (
    <div className="table-view-help">
      <div className="help-heading">
        <div className="partner-heading">{title} Help</div>
        <div className="searchbar-containers">
          <Tooltip placement="top" title='Allowed: letters, numbers, spaces, hyphens (-), and underscores (_).'>
            <Input
              className="searchbar"
              placeholder="Search Help Content"
              value={searchTerm}
              suffix={
                searchTerm ? (
                  <CloseOutlined
                    style={{ color: "#CC0E2D", verticalAlign: "middle", cursor: 'pointer' }}
                    onClick={handleClearSearch}
                  />
                ) : (
                  <SearchOutlined
                    style={{ color: "#CC0E2D", verticalAlign: "middle" }}
                  />
                )
              }
              onChange={handleSearchChange}
              style={{ width: "328px" }}
              maxLength={inputFieldCharLimit}
            />
          </Tooltip>

          <Button
            className="add-partner-btn redBtn"
            onClick={() => handleAddNewHelp()}
          >
            Add New Help
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="data-loader">
          <Spin className="loading-svg" tip="Loading" size="large" />
        </div>
      ) : (
        <div className={tabData?.length > 0 ? 'tableHasData' : 'tableNoData'}>
          {tabData?.length > 0 ? (
            <Table
              dataSource={tabData}
              columns={columns}
              rowKey="id"
              locale={{
                emptyText: (
                  <div style={{ width: "100%", textAlign: "center" }}>
                    No data available
                  </div>
                ),
              }}
            />
          ) : (
            <div className="no-data-message">
              <h3 style={{ textAlign: "center", margin: "50px 0" }}>{searchTerm ? `No results found for "${searchTerm}"` : "No help data available"}</h3>
            </div>
          )}
          {isModalOpen && (
            <ModalMediaPlayer
              closeModal={handleCancel}
              mediaLink={videoUrl}
              mediaType={mediatype}
            />
          )}
        </div>
      )}
    </div>
  );
}